import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface Project {
  id: number
  name: string
  slug: string
}

interface ArchiveProjectDialogProps {
  project: Project
  open: boolean
  onOpenChange: (open: boolean) => void
  archiveEndpoint?: string
}

export function ArchiveProjectDialog({
  project,
  open,
  onOpenChange,
  archiveEndpoint
}: ArchiveProjectDialogProps) {
  const { t } = useTranslation('projects')
  const [isArchiving, setIsArchiving] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen)
    if (!newOpen) setConfirmText('')
  }

  const handleArchive = () => {
    setIsArchiving(true)
    const endpoint = archiveEndpoint || `/organization/projects/${project.id}/archive`
    router.post(endpoint, {}, {
      onFinish: () => {
        setIsArchiving(false)
        onOpenChange(false)
        setConfirmText('')
      }
    })
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('archiveDialog.title')}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-muted-foreground">
              <span dangerouslySetInnerHTML={{ __html: t('archiveDialog.description', { name: project.name }) }} />
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{t('archiveDialog.item1')}</li>
                <li>{t('archiveDialog.item2')}</li>
                <li>{t('archiveDialog.item3')}</li>
              </ul>
              <p className="mt-3 font-medium">
                {t('archiveDialog.warning')}
              </p>
              <div className="mt-4">
                <p className="text-sm font-medium text-foreground mb-2">
                  <span dangerouslySetInnerHTML={{ __html: t('archiveDialog.typeToConfirm', { slug: project.slug }) }} />
                </p>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={project.slug}
                  className="mt-1"
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isArchiving}>{t('common:buttons.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleArchive}
            disabled={isArchiving || confirmText !== project.slug}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isArchiving ? t('archiveDialog.archiving') : t('archiveDialog.archiveButton')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
